import GenericEventTracker from '../GenericEventTracker'
import { TrackingClient } from '../../index'
import { Identity, Tracker } from '../../types/tracker'
import { UserTraits } from '../../types/traits'
import { Event } from '../../events'
import UnderlyingAsayerTracker, {
  Options as UnderlyingAsayerTrackerOptions,
} from '@asayerio/tracker'
import asayerGraphQLTrackerPlugin from '@asayerio/tracker-graphql'
import hashIndex from '@deal/hash-index'

export interface AsayerTrackerOptions extends UnderlyingAsayerTrackerOptions {
  sampleRate?: number
}

export default class AsayerTracker extends GenericEventTracker implements Tracker {
  private client: TrackingClient
  private options: AsayerTrackerOptions
  private asayer: UnderlyingAsayerTracker
  private asayerGraphQLTrackerFunction: ReturnType<ReturnType<typeof asayerGraphQLTrackerPlugin>>

  public constructor(client: TrackingClient, options: AsayerTrackerOptions) {
    super()

    this.client = client
    this.options = options

    this.asayer = new UnderlyingAsayerTracker(this.options)

    this.asayer.userAnonymousID(this.client.sessionId)

    if (this.client.userId) {
      this.asayer.userID(this.client.userId)
    }

    this.asayerGraphQLTrackerFunction = this.asayer.use(asayerGraphQLTrackerPlugin())

    if (this.shouldRecord()) {
      this.record()
    }
  }

  /**
   * Should session recording/playback be enabled for the tracked user?
   */
  private shouldRecord() {
    if (this.client.authenticated) {
      return true
    }

    if (!this.client.sessionId) {
      return false
    }

    // Generate a consistent number between 0 and 99 (inclusive)
    const hash = hashIndex(this.client.sessionId, 100)

    return hash < (this.options.sampleRate || 0.1) * 100
  }

  /**
   * Start recording the session
   */
  public record() {
    this.asayer.start()
  }

  /**
   * Identify the tracked user
   */
  public identify(identity: Identity) {
    this.asayer.userID(identity.userId)
    this.record()
  }

  /**
   * Associate traits with the tracked user
   */
  public addTraits(traits: UserTraits) {
    // Include name
    if (traits.firstName || traits.lastName) {
      this.asayer.metadata('name', [traits.firstName, traits.lastName].join(' ').trim())
    }

    // Include email
    if (traits.email) {
      this.asayer.metadata('email', traits.email)
    }

    // Include gender
    if (traits.gender) {
      this.asayer.metadata('gender', traits.gender)
    }

    // Include address parts (street, city, state, country, and postal code)
    if (traits.address) {
      for (let addressPart in traits.address) {
        if (traits.address[addressPart as keyof UserTraits['address']]) {
          this.asayer.metadata(
            addressPart,
            traits.address[addressPart as keyof UserTraits['address']]
          )
        }
      }
    }
  }

  /**
   * Track our custom events
   */
  public track(event: Event) {
    this.asayer.event(event.displayName, event.properties)
  }

  /**
   * Record a GraphQL operation (and result) to Asayer.
   *
   * See: https://docs.asayer.io/plugins/graphql
   */
  public recordGraphQLOperation(
    operationKind: string,
    operationName: string,
    variables: any,
    result: any
  ) {
    return this.asayerGraphQLTrackerFunction(operationKind, operationName, variables, result)
  }
}
